import { MQ } from '@common/constants/mq.constant';
import { THEME } from '@common/constants/theme.constant';
import { SerializedStyles } from '@emotion/react';
import { Collapse as AntdCollapse, CollapseProps } from 'antd';
import React, { ReactNode } from 'react';
import { css } from 'twin.macro';

const { Panel: AntdPanel } = AntdCollapse;

export interface ICollapseItem {
    component: React.FC;
    name: string;
    title: ReactNode;
}

export const CollapsePanel = AntdPanel;

interface IProps {
    defaultActiveKey?: string;
    items?: ICollapseItem[];
    children?: ReactNode;
    accordion?: boolean | undefined;
    onChange?: CollapseProps['onChange'];
    ghost?: CollapseProps['ghost'];
    className?: string;
    expandIconPosition?: CollapseProps['expandIconPosition'];
}

export const Collapse = ({
    items,
    ghost,
    accordion,
    children,
    defaultActiveKey,
    onChange,
    className,
    expandIconPosition = 'left',
}: IProps): JSX.Element => {
    const renderHeader = (title: ReactNode): ReactNode => {
        return title;
    };

    return (
        <AntdCollapse
            className={className}
            ghost={ghost}
            accordion={accordion}
            onChange={onChange}
            css={styles.root(ghost)}
            expandIconPosition={expandIconPosition}
            defaultActiveKey={defaultActiveKey || (items && items[0] ? [items[0].name] : undefined)}
        >
            {items?.map((item) => {
                return (
                    <AntdPanel header={renderHeader(item.title)} key={item.name}>
                        <item.component />
                    </AntdPanel>
                );
            })}
            {children}
        </AntdCollapse>
    );
};

const styles = {
    root: (ghost: boolean | undefined): (SerializedStyles | boolean)[] => [
        css`
            margin-bottom: 10px;

            &&.ant-collapse {
                border: 0px;
            }

            .ant-collapse-item {
                border: 0px;
            }
            .ant-collapse-content {
                font-weight: lighter;
                border: 0px;
                ${MQ.md} {
                    border: 1px solid ${THEME.GRAY_300};
                    border-top: 0px;
                }
            }
            .ant-collapse-content-box {
                padding: 0px 0px 16px 0px;

                ${MQ.md} {
                    padding: 16px;
                }
            }
        `,
        ghost
            ? css`
                  margin-bottom: 0px;
                  && .ant-collapse-header {
                      padding: 16px 0px 8px 0px;
                      font-size: 18px;
                      font-weight: 400;
                  }
                  && .ant-collapse-item {
                      border-bottom: 1px solid ${THEME.GRAY_100};

                      &:first-of-type .ant-collapse-header {
                          padding-top: 0px;
                      }

                      &:last-of-type {
                          border-bottom: none;
                      }
                  }
                  .ant-collapse-content-box {
                      padding: 0px !important;
                      padding-bottom: 12px !important;
                  }
                  .ant-collapse-item > .ant-collapse-header .anticon {
                      transform: rotate(90deg);
                  }
                  .ant-collapse-item-active > .ant-collapse-header .anticon {
                      transform: rotate(180deg);
                  }
                  .ant-collapse-content {
                      border: 0px;
                      padding-left: 0px;
                      ${MQ.md} {
                          border-top: 0px;
                      }
                  }
              `
            : css`
                  && .ant-collapse-header {
                      background-color: ${THEME.GREEN};
                      color: white;
                      font-weight: 200;
                      display: flex;
                      flex-shrink: 0;
                      width: 100%;

                      .ant-collapse-arrow {
                          padding-top: 6px;
                      }
                  }
              `,
    ],
};
